import './History.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso, VirtuosoHandle, Components } from 'react-virtuoso';
import { HistoryProps } from '$core/Chat/History/History.interface';
import { Placeholder } from '$uikit';
import { useSelector } from 'react-redux';
import { RootState } from '$store/rootReducer';
import {
  imHistorySelector,
  imInfoLoadingSelector,
  imShowSubscriptionRequiredMessageSelector,
} from '$store/im';
import {
  useChatInfo,
  useImActions,
  usePreparedHistory,
  useTranslator,
  useUserInfo,
} from '$hooks';
import { Loader, UnitName } from '$shared/components';
import { Message } from '$core/Chat/Message/Message';
import { formatDate } from '$utils';
import {
  registerVirtuoso,
  setVirtuosoAtBottom,
  setVirtuosoElement,
  unregisterVirtuoso,
} from '$utils/virtuosoRegistry';
import { SCROLL_TO_MESSAGE_EVENT } from '$core/Chat/replyPreview';
import { AttachType, MessageModel } from '$store/models';
import { PreparedHistoryItem } from '$hooks/usePreparedHistory';
import { useRoute } from 'react-router5';
import { SubscriptionRequiredMessage } from '$core/Chat/SubscriptionRequiredMessage/SubscriptionRequiredMessage';
import { mainSelector } from '$store/main';
import { ValentineDaySticker } from '$core/Chat/ValentineDaySticker/ValentineDaySticker';
import { Gift } from '$core/Chat/Gift/Gift';
import { ServiceNotification } from '$core/Chat/ServiceNotification/ServiceNotification';

type Translator = ReturnType<typeof useTranslator>;

const isNeedGroup = (message: PreparedHistoryItem, otherMessage: PreparedHistoryItem) => {
  let isGrouping = false;

  if (message.type !== otherMessage.type || message.type !== 'message') {
    return false;
  }

  if (
    (otherMessage.object as MessageModel).isInbox ===
    (message.object as MessageModel).isInbox
  ) {
    isGrouping = true;
  }

  return isGrouping;
};

const renderPreparedItem = (
  item: PreparedHistoryItem,
  peerId: number,
  t: Translator,
  isGroupingBefore: boolean,
  isGroupingAfter: boolean,
  isHighlighted = false,
) => {
  if (item.type === 'date') {
    return (
      <div className="History__date">
        {formatDate(new Date(item.object as number), t)}
      </div>
    );
  }

  const message = item.object as MessageModel;
  const attachments = message?.attachments;
  if (attachments && attachments.length > 0) {
    const type = attachments[0].type;
    if (type === AttachType.VALENTINE_DAY_STICKER) {
      return <ValentineDaySticker message={message} />;
    }
    if (type === AttachType.GIFT) {
      return <Gift message={message} peerId={peerId} isHighlighted={isHighlighted} />;
    }
    if (type === AttachType.SERVICE_NOTIFICATION || type === AttachType.SERVICE_SUMMARY) {
      return <ServiceNotification message={message} />;
    }
  }

  return (
    <Message
      message={message}
      peerId={peerId}
      isGroupingBefore={isGroupingBefore}
      isGroupingAfter={isGroupingAfter}
      isHighlighted={isHighlighted}
    />
  );
};

export const Messages = ({
  history,
  peerId,
}: {
  history: PreparedHistoryItem[];
  peerId: number;
}) => {
  const t = useTranslator();

  return (
    <>
      {history.map((item, index) => {
        const isGroupingBefore = index > 0 && isNeedGroup(item, history[index - 1]);
        const isGroupingAfter =
          index < history.length - 1 && isNeedGroup(item, history[index + 1]);

        return (
          <React.Fragment key={item.key}>
            {renderPreparedItem(item, peerId, t, isGroupingBefore, isGroupingAfter)}
          </React.Fragment>
        );
      })}
    </>
  );
};

type ChatItem = PreparedHistoryItem & {
  isGroupingBefore: boolean;
  isGroupingAfter: boolean;
};

interface ChatContext {
  peerId: number;
  showSubscriptionRequired: boolean;
  highlightedId: number | null;
}

const START_INDEX = 1_000_000;
const HIGHLIGHT_DURATION = 1500;
// virtuoso delays this check on resize only. Prepend scrollBy ignores it.
const HOLD_PREPEND =
  typeof navigator !== 'undefined' &&
  /iP(ad|od|hone)/i.test(navigator.userAgent) &&
  /WebKit/i.test(navigator.userAgent);

const ChatFooter: React.FC<{ context?: ChatContext }> = ({ context }) => {
  if (!context) {
    return null;
  }

  return (
    <>
      {context.showSubscriptionRequired && (
        <SubscriptionRequiredMessage peerId={context.peerId} />
      )}
      <div style={{ height: 'var(--send-form-height, 66px)' }} />
    </>
  );
};

interface ChatScrollerProps extends React.HTMLAttributes<HTMLDivElement> {
  context?: ChatContext;
}

const ChatScroller = React.forwardRef<HTMLDivElement, ChatScrollerProps>((props, ref) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { context, style, ...rest } = props;
  return (
    <div {...rest} ref={ref} id="chat_history" style={{ ...style, paddingTop: 16 }} />
  );
});
ChatScroller.displayName = 'ChatScroller';

const VIRTUOSO_COMPONENTS: Components<ChatContext> = {
  Footer: ChatFooter,
  Scroller: ChatScroller,
};

export const History: FC<HistoryProps> = ({ peerId }) => {
  const historyRaw = useSelector((state: RootState) => imHistorySelector(state, peerId));
  const isInfoLoading = useSelector((root: RootState) =>
    imInfoLoadingSelector(root, peerId),
  );
  const { isLoading, isLoadingMore, nextFrom } = useChatInfo(peerId);
  const { route } = useRoute();
  const t = useTranslator();
  const im = useImActions();
  const historyCopy = useMemo(
    () => Object.values(historyRaw) as MessageModel[],
    [historyRaw],
  );
  const history = usePreparedHistory(historyCopy);
  const peer = useUserInfo(peerId);

  const firstIndexRef = useRef(START_INDEX);
  const prevItemsRef = useRef<ChatItem[] | null>(null);
  const peerRef = useRef(peerId);
  const lastKeyRef = useRef<ChatItem['key'] | null>(null);
  const appendedOwnRef = useRef(false);
  const pinnedRef = useRef(true);
  const scrollingRef = useRef(false);
  const holdKeyRef = useRef<PreparedHistoryItem['key'] | null>(null);
  const historyRef = useRef(history);
  const [holdRev, setHoldRev] = useState(0);
  historyRef.current = history;

  if (peerRef.current !== peerId) {
    peerRef.current = peerId;
    firstIndexRef.current = START_INDEX;
    prevItemsRef.current = null;
    lastKeyRef.current = null;
    appendedOwnRef.current = false;
    pinnedRef.current = true;
    scrollingRef.current = false;
    holdKeyRef.current = null;
  }

  if (
    HOLD_PREPEND &&
    scrollingRef.current &&
    holdKeyRef.current == null &&
    prevItemsRef.current?.length
  ) {
    holdKeyRef.current = prevItemsRef.current[0].key;
  }

  const showSubscriptionRequired = useSelector((state: RootState) =>
    imShowSubscriptionRequiredMessageSelector(state, peerId),
  );
  const { decryptKeys } = useSelector(mainSelector);

  const items = useMemo<ChatItem[]>(() => {
    let source = history;
    const key = holdKeyRef.current;
    if (key != null) {
      const cut = history.findIndex((item) => item.key === key);
      if (cut > 0) {
        source = history.slice(cut);
      }
    }
    return source.map((item, index) => ({
      ...item,
      isGroupingBefore: index > 0 && isNeedGroup(item, source[index - 1]),
      isGroupingAfter: index < source.length - 1 && isNeedGroup(item, source[index + 1]),
    }));
  }, [history, holdRev]);

  const idToIndex = useMemo(() => {
    const map = new Map<number, number>();
    items.forEach((item, index) => {
      if (item.type === 'message') {
        map.set(Number(item.key), index);
      }
    });
    return map;
  }, [items]);

  // Track whether the freshest appended item is the user's own message, so
  // followOutput can pull the chat down after send even if scrolled up.
  const lastItem = items.length ? items[items.length - 1] : null;
  if ((lastItem?.key ?? null) !== lastKeyRef.current) {
    lastKeyRef.current = lastItem?.key ?? null;
    appendedOwnRef.current =
      lastItem?.type === 'message' && !(lastItem.object as MessageModel).isInbox;
  }

  const prevItems = prevItemsRef.current;
  if (items.length && prevItems && prevItems !== items) {
    for (let i = 0; i < prevItems.length; i++) {
      const prevItem = prevItems[i];
      if (prevItem.type !== 'message') {
        continue;
      }
      const newIdx = idToIndex.get(Number(prevItem.key));
      if (newIdx != null) {
        firstIndexRef.current -= newIdx - i;
        break;
      }
    }
  }
  prevItemsRef.current = items;
  const firstItemIndex = firstIndexRef.current;

  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const setVirtuosoRef = useCallback((handle: VirtuosoHandle | null) => {
    const prev = virtuosoRef.current;
    if (prev && prev !== handle) {
      unregisterVirtuoso('chat_history', prev);
    }
    virtuosoRef.current = handle;
    if (handle) {
      registerVirtuoso('chat_history', handle);
    }
  }, []);

  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const messageId = Number((e as CustomEvent).detail);
      const index = idToIndex.get(messageId);
      if (index == null) {
        return;
      }

      virtuosoRef.current?.scrollToIndex({
        index,
        align: 'center',
        behavior: 'smooth',
      });

      setHighlightedId(messageId);
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
      highlightTimerRef.current = setTimeout(
        () => setHighlightedId(null),
        HIGHLIGHT_DURATION,
      );
    };

    window.addEventListener(SCROLL_TO_MESSAGE_EVENT, handler);
    return () => window.removeEventListener(SCROLL_TO_MESSAGE_EVENT, handler);
  }, [idToIndex]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  const handledScrollToRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const target = route.params?.scrollTo;
    if (!target) {
      handledScrollToRef.current = undefined;
      return;
    }
    if (handledScrollToRef.current === target) {
      return;
    }
    const index = idToIndex.get(Number(target));
    if (index == null) {
      return;
    }
    handledScrollToRef.current = target;
    virtuosoRef.current?.scrollToIndex({
      index,
      align: 'center',
      behavior: 'smooth',
    });
  }, [route.params?.scrollTo, idToIndex]);

  const loadOlder = useCallback(() => {
    if (!isLoading && !isLoadingMore && !!nextFrom && !route.params.w) {
      im.loadMoreHistory(peerId, nextFrom);
    }
  }, [isLoading, isLoadingMore, nextFrom, route.params.w, peerId, im]);

  const onScrolling = useCallback((scrolling: boolean) => {
    if (!HOLD_PREPEND) {
      return;
    }
    scrollingRef.current = scrolling;
    if (scrolling || holdKeyRef.current == null) {
      return;
    }
    const key = holdKeyRef.current;
    holdKeyRef.current = null;
    if (historyRef.current.findIndex((item) => item.key === key) > 0) {
      setHoldRev((n) => n + 1);
    }
  }, []);

  const renderItem = useCallback(
    (_index: number, item: ChatItem, ctx: ChatContext) =>
      renderPreparedItem(
        item,
        peerId,
        t,
        item.isGroupingBefore,
        item.isGroupingAfter,
        item.type === 'message' &&
          (item.object as MessageModel).id === ctx?.highlightedId,
      ),
    [peerId, t],
  );

  const context = useMemo<ChatContext>(
    () => ({
      peerId,
      showSubscriptionRequired:
        showSubscriptionRequired && !decryptKeys[-1] && !peer?.isService,
      highlightedId,
    }),
    [peerId, showSubscriptionRequired, decryptKeys, peer?.isService, highlightedId],
  );

  if ((isLoading && !history.length) || isInfoLoading) {
    return <Loader isWrapped />;
  }

  if (!history.length) {
    return <Placeholder centered>{t('history_empty')}</Placeholder>;
  }

  if (peer && peer.isBanned) {
    return (
      <Placeholder>
        <UnitName unit={peer} /> is banned.
      </Placeholder>
    );
  }

  return (
    <div className="History__wrap">
      {isLoadingMore && (
        <div className="History__loadMore">
          <Loader mode="additional" />
        </div>
      )}
      <Virtuoso<ChatItem, ChatContext>
        key={peerId}
        ref={setVirtuosoRef}
        scrollerRef={(el) =>
          setVirtuosoElement('chat_history', el instanceof HTMLElement ? el : null)
        }
        className="History"
        data={items}
        context={context}
        components={VIRTUOSO_COMPONENTS}
        computeItemKey={(_index, item) => item.key}
        itemContent={renderItem}
        firstItemIndex={firstItemIndex}
        initialTopMostItemIndex={Math.max(0, items.length - 1)}
        alignToBottom
        followOutput={() =>
          pinnedRef.current || appendedOwnRef.current ? 'auto' : false
        }
        atBottomThreshold={80}
        atBottomStateChange={(atBottom) => {
          pinnedRef.current = atBottom;
          if (atBottom) {
            appendedOwnRef.current = false;
          }
          setVirtuosoAtBottom('chat_history', atBottom);
        }}
        isScrolling={onScrolling}
        startReached={loadOlder}
        increaseViewportBy={{ top: 600, bottom: 600 }}
      />
    </div>
  );
};
